import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ConsoleShell, PageHead } from "../components/ConsoleShell.js";
import { KpiCard, Tag, Tabs, TabPanel, Btn, BtnLink, Card } from "../components/index.js";
import { Banner } from "../components/Banner.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { QuizCard, scoreQuiz, PASS_THRESHOLD } from "../components/Quiz.js";
import { useAsync } from "../lib/useAsync.js";
import {
  manuals,
  books as booksApi,
  stores as storesApi,
  progress as progressApi,
  members as membersApi,
  type QuizQuestion,
  type MemberDetail,
} from "../lib/api.js";
import { isOrphanDraft } from "../lib/manualFilters.js";
import type { Book, Manual, ReadingProgressSummaryRow, Store } from "@tebiki/shared";

type LearningData = [Book[], Manual[], Store[], ReadingProgressSummaryRow[], MemberDetail[]];

/** Does this membership fall inside the book's audience? Empty audience = everyone. */
function memberInAudience(b: Book, m: MemberDetail): boolean {
  const storeOk =
    b.audience.storeIds.length === 0 ||
    m.membership.storeId === null || // org-wide membership
    b.audience.storeIds.includes(m.membership.storeId);
  const roleOk = b.audience.roles.length === 0 || b.audience.roles.includes(m.membership.role);
  return storeOk && roleOk;
}

/**
 * 学習管理 — REAL data: 必読の本 (books.mustRead) × 対象メンバー × サーバー同期された読了/クイズ
 * 進捗 (reading_progress)。割当 = 必読本を作る/対象を設定する行為そのもの（書架 → 本の設定）。
 */
export function Learning() {
  const [tab, setTab] = useState("assign");

  const { data, loading, error, offline, reload } = useAsync<LearningData>(async () => {
    const [bookList, manualList, storeList, rows, memberList] = await Promise.all([
      booksApi.list().catch(() => [] as Book[]),
      manuals.list().then((r) => r.items.filter((m) => !isOrphanDraft(m.status, m.title))),
      storesApi.list().catch(() => [] as Store[]),
      progressApi.summary().catch(() => [] as ReadingProgressSummaryRow[]),
      membersApi.listDetailed().catch(() => [] as MemberDetail[]),
    ]);
    return [bookList, manualList, storeList, rows, memberList];
  }, []);
  const [books, manualList, storeList, rows, memberList] = data ?? [[], [], [], [], []];

  const mustBooks = useMemo(() => books.filter((b) => b.mustRead), [books]);
  const manualById = useMemo(() => new Map(manualList.map((m) => [m.manualId, m])), [manualList]);
  const userName = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of memberList) if (d.user) m.set(d.user.id, d.user.displayName || d.user.email);
    return (id: string) => m.get(id) ?? `メンバー ${id.slice(-4)}`;
  }, [memberList]);
  const storeName = (id: string) => storeList.find((s) => s.id === id)?.name ?? "店舗";

  /** completedAt per (userId, manualId) from the server mirror. */
  const doneByUser = useMemo(() => {
    const map = new Map<string, Map<string, ReadingProgressSummaryRow>>();
    for (const r of rows) {
      let inner = map.get(r.userId);
      if (!inner) {
        inner = new Map();
        map.set(r.userId, inner);
      }
      inner.set(r.manualId, r);
    }
    return map;
  }, [rows]);

  interface AssignRow {
    book: Book;
    targetLabel: string;
    audience: MemberDetail[];
    /** manuals that actually exist (deleted/hidden ids excluded) */
    count: number;
    /** fraction of audience members who 修了 every manual in the book */
    donePct: number;
  }
  const assignRows: AssignRow[] = useMemo(() => {
    // Distinct users so a person with two memberships isn't counted twice.
    const uniqueMembers = [...new Map(memberList.filter((d) => d.user).map((d) => [d.user!.id, d])).values()];
    return mustBooks.map((b) => {
      const audience = uniqueMembers.filter((d) => memberInAudience(b, d));
      const ids = b.manualIds.filter((mid) => manualById.has(mid));
      const doneCount = audience.filter((d) => {
        const mine = doneByUser.get(d.user!.id);
        return ids.length > 0 && ids.every((mid) => !!mine?.get(mid)?.completedAt);
      }).length;
      const targetLabel =
        (b.audience.storeIds.length === 0 ? "全店" : b.audience.storeIds.map(storeName).join("・")) +
        `（${audience.length}名）`;
      return { book: b, targetLabel, audience, count: ids.length, donePct: audience.length ? Math.round((doneCount / audience.length) * 100) : 0 };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mustBooks, memberList, doneByUser, manualById, storeList]);

  const kpi = useMemo(() => {
    const audienceUnion = new Set(assignRows.flatMap((r) => r.audience.map((d) => d.user!.id)));
    const completions = rows.filter((r) => r.completedAt).length;
    const avg = assignRows.length
      ? Math.round(assignRows.reduce((s, r) => s + r.donePct, 0) / assignRows.length)
      : 0;
    return { assigned: mustBooks.length, users: audienceUnion.size, avg, completions };
  }, [assignRows, rows, mustBooks]);

  /** 受講状況: member × must-read book progress. */
  interface ProgRow {
    userId: string;
    book: Book;
    done: number;
    total: number;
  }
  const progRows: ProgRow[] = useMemo(() => {
    const out: ProgRow[] = [];
    for (const r of assignRows) {
      const ids = r.book.manualIds.filter((mid) => manualById.has(mid));
      for (const d of r.audience) {
        const mine = doneByUser.get(d.user!.id);
        const done = ids.filter((mid) => !!mine?.get(mid)?.completedAt).length;
        out.push({ userId: d.user!.id, book: r.book, done, total: ids.length });
      }
    }
    return out.sort((a, b) => a.done / Math.max(1, a.total) - b.done / Math.max(1, b.total));
  }, [assignRows, doneByUser, manualById]);

  /** 証跡: every completion event (user × manual, newest first). */
  const certRows = useMemo(
    () =>
      rows
        .filter((r) => r.completedAt)
        .sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0))
        .slice(0, 100),
    [rows],
  );

  return (
    <ConsoleShell
      breadcrumb={["TEBIKI", "学習管理"]}
      actions={
        <BtnLink to="/library" variant="primary" icon={<Icon name="plus" />} title="書架で本を作り「必読」にすると割当になります">
          必読の本を作る
        </BtnLink>
      }
    >
      <PageHead title={<>学習<span className="hl">・習熟管理</span></>}>
        必読の本の割当、受講状況、修了証跡、理解度クイズを一元管理。割当 = 「必読の本」×「対象店舗」。
      </PageHead>

      <Tabs
        active={tab}
        onChange={setTab}
        ariaLabel="学習ビュー"
        tabs={[
          { id: "assign", label: "割当" },
          { id: "progress", label: "受講状況" },
          { id: "cert", label: "証跡・修了" },
          { id: "quiz", label: "クイズ" },
        ]}
      />

      <div className="grid g-4" style={{ marginBottom: 20 }}>
        <KpiCard label="必読の本" value={kpi.assigned} suffix="冊" className="reveal" />
        <KpiCard label="対象メンバー" value={kpi.users} suffix="名" className="reveal d1" />
        <KpiCard label="平均修了率" value={`${kpi.avg}%`} className="reveal d2" />
        <KpiCard label="修了記録" value={kpi.completions} suffix="件" className="reveal d3" />
      </div>

      {loading ? (
        <LoadingState label="受講データを読み込み中…" />
      ) : error ? (
        <ErrorState message={error} offline={offline} onRetry={reload} />
      ) : tab === "quiz" ? (
        <TabPanel id="quiz" active={tab}>
          <QuizPanel />
        </TabPanel>
      ) : tab === "assign" ? (
        <TabPanel id="assign" active={tab}>
        {assignRows.length === 0 ? (
          <EmptyState icon={<Icon name="learning" size={30} />} title="必読の本がまだありません">
            書架で本を作り、「本の設定」から必読にして対象店舗を選ぶと、ここに割当として並びます。
            <div style={{ marginTop: 16 }}>
              <BtnLink to="/library" variant="primary" icon={<Icon name="library" size={16} />}>書架へ</BtnLink>
            </div>
          </EmptyState>
        ) : (
          <div className="list reveal d1">
            <div className="lrow lhead acols" style={acols}>
              <span>必読の本 / 対象</span>
              <span>冊数</span>
              <span>修了率</span>
              <span />
            </div>
            {assignRows.map((r) => (
              <div key={r.book.bookId} className="lrow acols" style={acols}>
                <div>
                  <b className="jp">{r.book.title}</b>
                  <div className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
                    対象: {r.targetLabel}
                  </div>
                </div>
                <Tag mono>{r.count}冊</Tag>
                <div className="row">
                  <div className="pmini grow">
                    <i style={{ width: `${r.donePct}%`, background: r.donePct < 50 ? "var(--warn)" : undefined }} />
                  </div>
                  <span className="mono" style={{ fontSize: 11 }}>
                    {r.donePct}%
                  </span>
                </div>
                <Link className="tag" to={`/book/${r.book.bookId}`}>
                  本を開く
                </Link>
              </div>
            ))}
          </div>
        )}
        </TabPanel>
      ) : tab === "progress" ? (
        <TabPanel id="progress" active={tab}>
        {progRows.length === 0 ? (
          <EmptyState icon={<Icon name="members" size={30} />} title="受講対象がまだありません">
            必読の本と対象店舗を設定すると、メンバーごとの進捗がここに並びます。
          </EmptyState>
        ) : (
          <div className="list reveal d1">
            <div className="lrow lhead acols" style={acols}>
              <span>メンバー / 本</span>
              <span>進捗</span>
              <span>状態</span>
              <span />
            </div>
            {progRows.map((r) => {
              const pct = r.total ? Math.round((r.done / r.total) * 100) : 0;
              return (
                <div key={`${r.userId}:${r.book.bookId}`} className="lrow acols" style={acols}>
                  <div>
                    <b className="jp">{userName(r.userId)}</b>
                    <div className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>{r.book.title}</div>
                  </div>
                  <div className="row">
                    <div className="pmini grow">
                      <i style={{ width: `${pct}%`, background: pct < 50 ? "var(--warn)" : undefined }} />
                    </div>
                    <span className="mono" style={{ fontSize: 11 }}>{r.done}/{r.total}</span>
                  </div>
                  {pct >= 100 && r.total > 0 ? (
                    <Tag dot color="var(--ok)">修了</Tag>
                  ) : pct > 0 ? (
                    <Tag dot color="var(--info)">進行中</Tag>
                  ) : (
                    <Tag>未着手</Tag>
                  )}
                  <Link className="tag" to={`/book/${r.book.bookId}`}>本を開く</Link>
                </div>
              );
            })}
          </div>
        )}
        </TabPanel>
      ) : (
        <TabPanel id="cert" active={tab}>
        {certRows.length === 0 ? (
        <EmptyState icon={<Icon name="approval" size={30} />} title="修了記録はまだありません">
          スタッフがマニュアルを読了しクイズに合格すると、修了の証跡がここに記録されます。
        </EmptyState>
        ) : (
        <div className="list reveal d1">
          <div className="lrow lhead acols" style={acols}>
            <span>メンバー / マニュアル</span>
            <span>クイズ</span>
            <span>修了日</span>
            <span />
          </div>
          {certRows.map((r) => (
            <div key={`${r.userId}:${r.manualId}`} className="lrow acols" style={acols}>
              <div>
                <b className="jp">{userName(r.userId)}</b>
                <div className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
                  {manualById.get(r.manualId)?.title ?? r.manualId}
                </div>
              </div>
              <Tag mono>{r.quizScore != null ? `${r.quizScore}点` : "—"}</Tag>
              <span className="mono" style={{ fontSize: 12 }}>
                {r.completedAt ? new Date(r.completedAt).toLocaleDateString("ja-JP") : "—"}
              </span>
              <Link className="tag" to={`/read/${r.manualId}`}>開く</Link>
            </div>
          ))}
        </div>
        )}
        </TabPanel>
      )}

      {tab === "assign" ? (
        <Banner variant="info" icon={<Icon name="ai-review" />} className="reveal d2">
          <span>
            <b>AIクイズ自動生成:</b> 各マニュアルの手順から理解度確認の設問を生成できます（合格基準: {PASS_THRESHOLD}%）。「クイズ」タブで実行できます。
          </span>
        </Banner>
      ) : null}
    </ConsoleShell>
  );
}

const acols = { gridTemplateColumns: "1fr 130px 1fr 100px" } as const;

// ---------- quiz (QuizCard / scoreQuiz / PASS_THRESHOLD shared via components/Quiz) ----------

/**
 * AI quiz tab: pick a manual, generate a 4-choice comprehension quiz, answer it inline with
 * immediate correct/incorrect feedback + explanation, and tally a pass/fail score. Loading and
 * demo-fallback (when the generator API is unreachable) are handled gracefully.
 */
function QuizPanel() {
  const list = useAsync<Manual[]>(async () => (await manuals.list()).items, []);
  const manualsList = list.data ?? [];
  const [manualId, setManualId] = useState("");
  const effectiveId = manualId || manualsList[0]?.manualId || "";

  const [questions, setQuestions] = useState<QuizQuestion[] | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [generating, setGenerating] = useState(false);
  const [quizError, setQuizError] = useState<string | null>(null);

  const result = useMemo(
    () => (questions ? scoreQuiz(questions, answers) : null),
    [questions, answers],
  );
  const allAnswered = questions ? Object.keys(answers).length === questions.length : false;

  async function generate() {
    if (!effectiveId) return;
    setGenerating(true);
    setQuestions(null);
    setAnswers({});
    setQuizError(null);
    try {
      const res = await manuals.quiz(effectiveId);
      setQuestions(res.questions);
    } catch {
      setQuizError("クイズの生成に失敗しました。通信環境を確認して、もう一度お試しください。");
    } finally {
      setGenerating(false);
    }
  }

  function answer(qi: number, ci: number) {
    // Lock in the first answer per question so scoring stays honest.
    setAnswers((prev) => (qi in prev ? prev : { ...prev, [qi]: ci }));
  }

  function reset() {
    setQuestions(null);
    setAnswers({});
    setQuizError(null);
  }

  return (
    // panel wrapping is the CALLER's job (Learning wraps this in <TabPanel id="quiz">) —
    // nesting a second tabpanel here duplicated the panel-quiz id.
    <div>
      <Banner variant="info" icon={<Icon name="ai-review" />} className="reveal">
        <span>
          <b>AIクイズ:</b> マニュアルの手順から理解度確認の4択問題を自動生成します（合格基準: {PASS_THRESHOLD}%）。
        </span>
      </Banner>

      <Card crop pad className="reveal d1" style={{ marginTop: 14 }}>
        <div className="spread" style={{ flexWrap: "wrap", gap: 14, alignItems: "end" }}>
          <div style={{ flex: "1 1 280px" }}>
            <label className="field-label" htmlFor="quiz-manual">
              マニュアル
            </label>
            {list.loading ? (
              <div className="mono" style={{ color: "var(--muted)", fontSize: 13 }}>
                読み込み中…
              </div>
            ) : (
              <select
                id="quiz-manual"
                className="select"
                value={effectiveId}
                onChange={(e) => {
                  setManualId(e.target.value);
                  reset();
                }}
              >
                {manualsList.map((m) => (
                  <option key={m.manualId} value={m.manualId}>
                    {m.title}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="row" style={{ gap: 10 }}>
            <Btn variant="primary" icon={<Icon name="ai-review" />} onClick={() => void generate()} disabled={generating || !effectiveId}>
              {generating ? "生成中…" : questions ? "再生成" : "クイズを生成"}
            </Btn>
          </div>
        </div>
      </Card>

      {quizError ? (
        <Banner variant="warn" icon={<Icon name="warning" />} className="reveal d1">
          <span>{quizError}</span>
        </Banner>
      ) : null}

      {generating ? (
        <LoadingState label="AIがクイズを生成中…" />
      ) : !questions ? (
        <EmptyState icon={<Icon name="ai-review" size={30} />} title="クイズはまだありません">
          マニュアルを選んで「クイズを生成」を押すと、理解度確認の設問が表示されます。
        </EmptyState>
      ) : !questions.length ? (
        <EmptyState title="設問を生成できませんでした">
          このマニュアルには十分な手順がない可能性があります。別のマニュアルでお試しください。
        </EmptyState>
      ) : (
        <>
          {result ? (
            <Card
              crop
              pad
              className="reveal d1"
              style={{ marginTop: 16 }}
              role="status"
              aria-live="polite"
            >
              <div className="spread" style={{ flexWrap: "wrap", gap: 12 }}>
                <div>
                  <div className="kicker">SCORE</div>
                  <div className="display" style={{ fontSize: 28, margin: "4px 0" }}>
                    {result.correct} / {result.total}
                    <span style={{ fontSize: 18, color: "var(--muted)", marginLeft: 10 }}>
                      {result.score}%
                    </span>
                  </div>
                </div>
                {allAnswered ? (
                  result.passed ? (
                    <Tag dot color="var(--ok)">
                      合格（{PASS_THRESHOLD}%以上）
                    </Tag>
                  ) : (
                    <Tag dot color="var(--danger)">
                      不合格（{PASS_THRESHOLD}%未満）
                    </Tag>
                  )
                ) : (
                  <Tag dot color="var(--hi)">
                    回答中 {Object.keys(answers).length}/{result.total}
                  </Tag>
                )}
              </div>
            </Card>
          ) : null}

          <div className="reveal d2" style={{ display: "grid", gap: 14, marginTop: 16 }}>
            {questions.map((q, qi) => (
              <QuizCard key={qi} index={qi} question={q} selected={answers[qi]} onAnswer={(ci) => answer(qi, ci)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
